import * as React from 'react';
import { useSelector } from 'react-redux';
import HtmlContent from '../_common/html-content';
import CustomLink from '../_common/custom-link';
import Typeahead from '../_common/typeahead';
import { getConfig, getText } from '../../helpers';
import { ReactComponent as SearchIcon } from '../../assets/svg/icons/search.svg';
import { ReactComponent as CaretRightIcon } from '../../assets/svg/icons/caret-right.svg';

const placeholder = getText('generic.typeAheadPlaceholder');
const or = getText('generic.or');
const langCode = getText('langCode');
const findRelevantProducts = getText('generic.findRelevantProducts');
const logInToAccessAiText = getText('pages.llm.logInToAccessAi');
const signInUrl = getConfig('urls.mi.ssacs.oktaSignIn');

function homepageSearchIcon() {
    return (
        <button type="submit" id="hero-product-finder-icon-btn" className="button button--icon-only button--secondary hero-product-finder__icon" aria-label="search-icon">
            <SearchIcon />
        </button>
    );
}

function homepageSearchIconActive(value: boolean) {
    return (
        <button type="submit" data-testid="hero-product-finder-redicon-btn" disabled={value} id="hero-product-finder-redicon-btn" className="button button--icon-only button--primary hero-product-finder__redicon" aria-label="search-icon">
            <SearchIcon />
        </button>
    );
}

function HeroProductFinder(props: HeroProductFinderProps & HeroVideoProps) {
    const { snippet, title, emptySearchSection, setSearchInputFocused } = props;
    const [showPopup, setShowPopup] = React.useState<boolean>(false);
    const [iconColor, setIconColor] = React.useState<boolean>(false);
    const [searchText, setSearchText] = React.useState<string>('');
    const [isLLMSearch, setIsLLMSearch] = React.useState<boolean>(false);
    const [disabledSearchIcon, setDisabledSearchIcon] = React.useState<boolean>(false);
    const [isGlobalClick, setIsGlobalClick] = React.useState<boolean>(false);
    const isLoggedIn = useSelector((st: RootStoreState) => st.user.isLoggedIn);
    const [isIpad, setIsIpad] = React.useState<boolean>(false);

    const checkIfTablet = () => {
        if (
            navigator.platform === 'iPhone' ||
            navigator.platform === 'iPad' ||
            (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 0) ||
            navigator.userAgent.includes('Android') ||
            (navigator.platform === 'Linux armv81' && 'ontouchstart' in window && window.innerWidth > window.innerHeight && window.innerHeight < 768)
        ) {
            setIsIpad(true);
        } else setIsIpad(false);
    };

    const handleResize = () => {
        checkIfTablet();
    };

    React.useEffect(() => {
        checkIfTablet();
    }, []);

    React.useEffect(() => {
        window.addEventListener('resize', handleResize);
        return () => {
            window.removeEventListener('resize', handleResize);
        };
    });

    const onSearchBarOpen = () => {
        setSearchInputFocused(true);
        setShowPopup(true);
        if (searchText.length > 0) setIconColor(true);
        else setIconColor(false);
    };

    const onSearchBarClose = () => {
        setSearchInputFocused(false);
        setShowPopup(false);
        setIconColor(false);
        setIsGlobalClick(false);
        const searchIcon = document.getElementById('hero-product-finder-icon-btn');
        const currentFocusElement = document.getElementById('hero-search-form');
        if (searchIcon && currentFocusElement === document.activeElement) searchIcon.focus();
    };

    const updateSearchIcon = (value: string, away: boolean) => {
        if (away) {
            if (value !== '') {
                setIconColor(true);
                setSearchText(value);
            } else {
                setIconColor(false);
                setSearchText('');
            }
        }
    };

    const onComplete = () => {
        onSearchBarClose();
    };

    React.useEffect(() => {
        // header search-icon click listener
        document.addEventListener('HeaderSearchClick', () => {
            onSearchBarOpen();
            setIsGlobalClick(true);
        });

        // TODO: urgent fix of mobile behavior, better to use global listener
        document.addEventListener('MobileSearchButtonClickEvent', () => {
            onSearchBarOpen();
            setIsGlobalClick(true);
        });

        // TODO: urgent fix of mobile behavior, better to use global listener
        document.addEventListener('MobileHeaderBodyClickEvent', () => {
            onSearchBarClose();
        });

        document.addEventListener('HeaderBodyClick', () => {
            if (showPopup) {
                onSearchBarClose();
            }
        });
    });

    const onSearchTypeChanged = (value: boolean) => {
        setIsLLMSearch(value);
    };

    const onDisableEnableSearchBtn = (value: boolean) => {
        setDisabledSearchIcon(value);
    };

    return (
        // eslint-disable-next-line no-nested-ternary
        <section className={`${showPopup ? (isLLMSearch ? 'active-blur-llm' : 'active-blur') : ''} hero-product-finder page-section`}>
            <div className="hero-product-finder__content-wrapper page-section__inner">
                <div className="hero-product-finder__content">
                    <div className="hero-product-finder__contentheader">
                        <div className="hero-product-finder__contentheader-left">
                            <HtmlContent className="hero-product-finder__title" html={title} inputProps={{ 'data-testid': 'hero-title' }} />
                        </div>
                        <div className="hero-product-finder__contentheader-right">
                            <div>
                                <HtmlContent className="hero-product-finder__desc" html={snippet} inputProps={{ 'data-testid': 'hero-desc' }} />
                            </div>
                        </div>
                    </div>
                    <div className="hero-product-finder__contentbody">
                        <article className={langCode === 'en' ? 'hero-product-finder__form__en hero-product-finder__form' : 'hero-product-finder__form'}>
                            <Typeahead
                                searchText={searchText}
                                iconAppend={iconColor ? homepageSearchIconActive(disabledSearchIcon) : homepageSearchIcon()}
                                name="hero-search-form"
                                placeholder={placeholder}
                                isMobileDropdown={false}
                                className="hero-product-finder__typeahead"
                                onChangeEvent={updateSearchIcon}
                                isModal
                                autoFocus={false}
                                onFocusEvent={onSearchBarOpen}
                                awayClose={showPopup}
                                emptySearchSection={emptySearchSection}
                                showSearchFilters={showPopup}
                                onComplete={onComplete}
                                isGlobalClick={isGlobalClick}
                                onSearchTypeChanged={onSearchTypeChanged}
                                onDisableEnableSearchBtn={onDisableEnableSearchBtn}
                            />
                        </article>
                        {!showPopup && (
                            <div>
                                <div className={langCode === 'en' ? 'hero-product-finder__productfinderor__en hero-product-finder__productfinderor' : 'hero-product-finder__productfinderor'}>
                                    <span className="hero-product-finder__seperator">
                                        <span>{or}</span>
                                    </span>
                                </div>
                                <div className={`hero-product-finder__productfinderbutton ${langCode === 'ja' ? 'jaLang' : ''} ${langCode === 'zh' ? 'zhLang' : ''}`}>
                                    <CustomLink
                                        url="/support/product-finder"
                                        text={findRelevantProducts}
                                        className="hero-product-finder__productFinderLink"
                                        buttonTheme="tertiary"
                                        isButton
                                        hasIcon={false}
                                        hasImage
                                        htmlText
                                    />
                                </div>
                            </div>
                        )}

                    </div>
                </div>
            </div>
            {showPopup && <button type="button" className="close-btn" onClick={onSearchBarClose} />}
        </section>
    );
}

export default React.memo(HeroProductFinder);
